import './RoleStageActionFlows.scss';
import Container from 'components/molecules/Container/Container';
import NavbarWithBackLink from 'components/molecules/NavbarWithBackLink/NavbarWithBackLink';
import { HelmetAndPageAnnouncer } from 'components/organisms/HelmetAndPageAnnouncer/HelmetAndPageAnnouncer';
import TestIntro from 'components/organisms/TestIntro/TestIntro';
import {
  SupportedLocale,
  useStartPersonalityTestIntegrityMonitoringSessionMutation,
  useUserQuery,
} from 'generated/graphql';
import { TestKind } from 'enums/tests';
import { useIntegrityCamera } from 'hooks/useIntegrityCamera';
import { StartIntegritySession } from 'hooks/useIntegrityMonitoring';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@spotted-zebra-uk/ui-components';
import config, {
  withIntegrityMonitoringNote,
} from 'views/Test/TestInfo/TestInfo.constants';
import PersonalityTestRunner from './PersonalityTestRunner';
import { actionCandidateStatusToTestStatus } from './RoleStageActionFlows.helpers';
import { useOnlineAssessmentIntegrityMonitoring } from './useOnlineAssessmentIntegrityMonitoring';
import { useRoleStageActionsFlow } from './useRoleStageActionsFlow';

type TTestState = {
  started: boolean;
  locale?: SupportedLocale;
};

const RoleStagePersonalityTest: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role, stage, loading, exiting, actionsUrl, thankYouUrl } =
    useRoleStageActionsFlow();

  const [testState, setTestState] = useState<TTestState>({ started: false });

  const userQueryResponse = useUserQuery();
  const assessmentLocale =
    userQueryResponse.data?.User.preferredLocale.locale ||
    SupportedLocale.English;

  const action = stage?.actions.personalityTestAction;

  // Camera-based integrity monitoring (project toggle): the camera is
  // requested on the intro page; the session starts on grant and runs until
  // this flow unmounts (test finished or left).
  const monitoringEnabled =
    (stage?.isIntegrityMonitoringEnabled ?? false) && !!action;
  const camera = useIntegrityCamera(monitoringEnabled);
  const [startIntegritySession] =
    useStartPersonalityTestIntegrityMonitoringSessionMutation();
  const actionId = action?.id;
  const startSession = useCallback<StartIntegritySession>(
    async (input) => {
      if (!actionId) return null;
      const { data } = await startIntegritySession({
        variables: {
          personalityTestAssessmentStageActionCandidateId: actionId,
          input,
        },
      });
      return data?.startPersonalityTestIntegrityMonitoringSession ?? null;
    },
    [actionId, startIntegritySession]
  );
  useOnlineAssessmentIntegrityMonitoring({
    enabled: monitoringEnabled,
    stream: camera.stream,
    startSession,
  });

  // The stage has no personality test — nothing to run here.
  useEffect(() => {
    if (stage && !action) {
      navigate(actionsUrl);
    }
  }, [stage, action, navigate, actionsUrl]);

  if (loading) {
    return (
      <Container className="page-loader-wrapper">
        <Loader variant="bubbles" />
      </Container>
    );
  }

  if (exiting || !role || !stage || !action) {
    return null;
  }

  if (!testState.started) {
    const singleTestConfig = config[TestKind.Personality]({
      timeRequirement: action.estimatedTimeSeconds,
      // TEMP HACK — "Personality" → "Behavioural" relabel (per-client,
      // temporary). See helpers/behaviouralRelabel.ts
      usesBehaviouralLabel: role.company.usesBehaviouralLabel,
    });
    const notes = monitoringEnabled
      ? withIntegrityMonitoringNote(singleTestConfig.notes)
      : singleTestConfig.notes;
    const MainIcon = singleTestConfig.icon;

    return (
      <Container className="TestInfoContainer">
        <NavbarWithBackLink
          to={actionsUrl}
          ariaLabel={t('assessment.returnToAllAssessmentActions')}
          label={t('assessment.actions')}
        />
        <HelmetAndPageAnnouncer
          pageTitle={t('test.introForTest', {
            actionName: singleTestConfig.title,
          })}
        />
        <TestIntro
          MainIcon={MainIcon}
          type={singleTestConfig.key}
          assessmentLocale={assessmentLocale}
          selectedLocales={action.selectedLocales}
          companyName={role.company.name}
          title={singleTestConfig.title}
          notes={notes}
          testStatus={actionCandidateStatusToTestStatus(action.status)}
          hasPractice={false}
          onStart={locale => setTestState({ started: true, locale })}
          testTimePerQuestion={0}
          cameraPermission={
            monitoringEnabled
              ? { granted: camera.granted, request: camera.request }
              : undefined
          }
        />
      </Container>
    );
  }

  return (
    <PersonalityTestRunner
      actionCandidateId={action.id}
      locale={testState.locale}
      exitUrl={actionsUrl}
      onFinishTest={() => navigate(thankYouUrl)}
      usesBehaviouralLabel={role.company.usesBehaviouralLabel}
    />
  );
};

export default RoleStagePersonalityTest;
